import {Checkbox} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';

import {StudySearchItem} from './StudySearchInput';

export interface StudyAutoLinkingInputProps {
  selectedStudies: Array<StudySearchItem>;
  invalidItemNames: Array<string>;
  isAutoLinkingChecked: boolean;
  isAutoLinkingDisabled: boolean;
  onAutoLinkingCheckedChange: (e: InputChangeEvent) => void;
  onAutoLinkingStudyChange: (studyId: string) => void;
  // The study that the item will be linked to
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
  enableAutoLinkingSelection: boolean;
  includeAutoLinkingStudyDropdown: boolean;
  // The layout of the auto-linking study selection dropdown.
  // If 'inline', the dropdown is displayed inline with the checkbox. If 'stacked', the dropdown is displayed below the checkbox.
  dropdownLayout?: 'inline' | 'stacked';
}

export default function StudyAutoLinkingInput({
  selectedStudies,
  invalidItemNames,
  isAutoLinkingChecked,
  isAutoLinkingDisabled,
  onAutoLinkingCheckedChange,
  onAutoLinkingStudyChange,
  autoLinkingStudy,
  enableAutoLinkingSelection,
  includeAutoLinkingStudyDropdown,
  dropdownLayout = 'inline',
}: StudyAutoLinkingInputProps): JSX.Element {
  const isMultipleSelectedItems = selectedStudies.length > 1;
  const filteredStudies = selectedStudies.filter((study) => !invalidItemNames.includes(study.name));
  return (
    <>
      {autoLinkingStudy && !enableAutoLinkingSelection && (
        <div className={css(styles.autoLinkingCheckbox)}>
          <CheckboxInput
            id="study-auto-link-checkbox"
            label={
              <div>
                Automatically link items created in this entry to <strong>{autoLinkingStudy.name}</strong>
              </div>
            }
            disabled={isAutoLinkingDisabled}
            checked={isAutoLinkingChecked}
            onChange={onAutoLinkingCheckedChange}
          />
        </div>
      )}
      {includeAutoLinkingStudyDropdown && autoLinkingStudy && enableAutoLinkingSelection && (
        <div
          className={css(
            dropdownLayout === 'inline' ? styles.inlineAutoLinkingSection : styles.stackedAutoLinkingSection
          )}
        >
          <>
            <Checkbox
              className={css(styles.autoLinkingCheckbox)}
              id="study-auto-link-checkbox"
              checked={isAutoLinkingChecked}
              onChange={onAutoLinkingCheckedChange}
              disabled={isAutoLinkingDisabled}
              label={
                <>
                  Automatically link items created in this entry to{' '}
                  {!isMultipleSelectedItems && <strong>{autoLinkingStudy.name}</strong>}
                </>
              }
            />
            {isMultipleSelectedItems && (
              <span
                className={css(
                  dropdownLayout === 'inline'
                    ? styles.inlineAutoLinkingDropdown
                    : styles.stackedAutoLinkingDropdown
                )}
              >
                <SelectDropdown
                  ariaLabel="Auto-linking study dropdown"
                  displayName={autoLinkingStudy.name}
                  menu={
                    <Menu
                      value={autoLinkingStudy.id}
                      width={Menu.WIDTHS.FIT_CONTENT}
                      className={css(styles.autoLinkingMenu)}
                    >
                      {filteredStudies.map((study) => (
                        <Option
                          key={study.id}
                          value={study.id}
                          onSelect={() => {
                            onAutoLinkingStudyChange(study.id);
                          }}
                        >
                          {study.name}
                        </Option>
                      ))}
                    </Menu>
                  }
                />
              </span>
            )}
          </>
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  inlineAutoLinkingSection: {
    alignItems: 'flex-start',
    display: 'flex',
    marginTop: 8,
  },
  stackedAutoLinkingSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 8,
    maxWidth: '100%',
    rowGap: '4px',
    width: 'fit-content',
  },
  autoLinkingCheckbox: {
    marginTop: 5,
  },
  inlineAutoLinkingDropdown: {
    flex: 1,
    marginLeft: 4,
    minWidth: 0,
  },
  stackedAutoLinkingDropdown: {
    maxWidth: '100%',
    minWidth: '100%',
    width: 0,
  },
  autoLinkingMenu: {
    minWidth: '100%',
  },
});
